import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import { Modal } from '../components/ui/modal';
import { useHeroImageStore } from '../store/useHeroImageStore';
import { useHistoryStore } from '../store/useHistoryStore';
import { useNewsStore } from '../store/useNewsStore';
import { useEventStore } from '../store/useEventStore';
import { useChurchInfoStore } from '../store/useChurchInfoStore';
import { useCouncilStore } from '../store/useCouncilStore';
import { useAuthStore } from '../store/useAuthStore';

const LandingPage: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedNewsItem, setSelectedNewsItem] = useState<string | null>(null);

  const navRef = useRef<HTMLElement>(null);
  const getHeroImages = useHeroImageStore((state) => state.getHeroImages);
  const heroImages = useHeroImageStore((state) => state.heroImages);
  const getHistories = useHistoryStore((state) => state.getHistories);
  const histories = useHistoryStore((state) => state.histories);
  const getNews = useNewsStore((state) => state.getNews);
  const news = useNewsStore((state) => state.news);
  const getEvents = useEventStore((state) => state.getEvents);
  const events = useEventStore((state) => state.events);
  const getChurchInfo = useChurchInfoStore((state) => state.getChurchInfo);
  const churchInfo = useChurchInfoStore((state) => state.churchInfo);
  const getCouncils = useCouncilStore((state) => state.getCouncils);
  const councils = useCouncilStore((state) => state.councils);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  useEffect(() => {
    // Fetch hero images on mount (public endpoint, no auth required)
    getHeroImages(true, true); // Only fetch active images, use public endpoint
    // Fetch histories on mount (public endpoint, no auth required)
    getHistories(true); // Use public endpoint
    // Fetch news on mount (public endpoint, no auth required)
    getNews(true); // Use public endpoint
    // Fetch events on mount (public endpoint, no auth required)
    getEvents(true); // Use public endpoint
    // Fetch church info on mount (public endpoint, no auth required)
    getChurchInfo(true); // Use public endpoint
    // Fetch councils on mount (public endpoint, no auth required)
    getCouncils(true); // Use public endpoint
  }, [getHeroImages, getHistories, getNews, getEvents, getChurchInfo, getCouncils]);

  // Debug: Log events when they change
  useEffect(() => {
    console.log('Events data:', events);
    console.log('Events count:', events?.length);
    if (events && events.length > 0) {
      events.forEach((event, index) => {
        console.log(`Event ${index} (${event.title}):`, {
          id: event._id,
          title: event.title,
          description: event.description,
          hasDescription: !!event.description,
          descriptionType: typeof event.description,
          descriptionLength: event.description?.length,
          descriptionTrimmed: event.description?.trim(),
          descriptionTrimmedLength: event.description?.trim().length,
          fullEvent: event,
        });
      });
    }
  }, [events]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      // Update active section based on scroll position
      const sections = ['home', 'welcome', 'history', 'news', 'events'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections.reverse()) {
        const element = document.getElementById(section);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(section);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false); // Close mobile menu after navigation
    }
  };

  // Fallback images if no hero images are available
  const fallbackImages = ['/images/church/5009.jpg'];

  // Use hero images from API if available, otherwise use fallback
  const displayImages =
    heroImages.length > 0
      ? heroImages
      : fallbackImages.map((img, idx) => ({
          _id: `fallback-${idx}`,
          image: img,
        }));

  // Priest images for history timeline
  const priestImages = [
    '/images/priests/2151103914.jpg',
    '/images/priests/dawn-mcdonald-LrwpNJ2yI-A-unsplash (2).jpg',
    '/images/priests/full-shot-priest-reading-bible.jpg',
    '/images/priests/tamara-govedarovic-buIaKqjiMZw-unsplash.jpg',
  ];

  // Sample events data

  // Use histories from API, fallback to static data if empty
  const historyTimeline =
    histories.length > 0
      ? histories.map((history) => ({
          period: history.period,
          priest: history.priest,
          description: history.description || '',
          image: history.image,
        }))
      : [];

  return (
    <div className="min-h-screen bg-white">
      {/* Sticky Navigation */}
      <nav ref={navRef} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg py-3' : 'py-5'}`}>
        {/* Black transparent overlay for hero section - only visible when not scrolled */}
        {!isScrolled && (
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/25 to-transparent h-[140px] md:h-[100px] pointer-events-none"></div>
        )}
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <Link to="/">
              <div
                className={`text-xl md:text-2xl font-bold transition-colors ${isScrolled ? '' : 'text-white'}`}
                style={{ fontFamily: 'Playfair Display, serif', color: isScrolled ? '#B8860B' : '#FFFFFF' }}
              >
                St Antony's Church
              </div>
            </Link>
            <div className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => scrollToSection('home')}
                className={`text-sm font-medium transition-colors ${
                  activeSection === 'home' ? 'text-[#B8860B]' : isScrolled ? 'text-gray-700 hover:text-[#B8860B]' : 'text-white hover:text-[#B8860B]'
                }`}
                style={{ fontFamily: 'Lato, sans-serif' }}
              >
                Home
              </button>
              <button
                onClick={() => scrollToSection('history')}
                className={`text-sm font-medium transition-colors ${
                  activeSection === 'history'
                    ? 'text-[#B8860B]'
                    : isScrolled
                    ? 'text-gray-700 hover:text-[#B8860B]'
                    : 'text-white hover:text-[#B8860B]'
                }`}
                style={{ fontFamily: 'Lato, sans-serif' }}
              >
                History
              </button>
              <button
                onClick={() => scrollToSection('news')}
                className={`text-sm font-medium transition-colors ${
                  activeSection === 'news' ? 'text-[#B8860B]' : isScrolled ? 'text-gray-700 hover:text-[#B8860B]' : 'text-white hover:text-[#B8860B]'
                }`}
                style={{ fontFamily: 'Lato, sans-serif' }}
              >
                News
              </button>
              <button
                onClick={() => scrollToSection('events')}
                className={`text-sm font-medium transition-colors ${
                  activeSection === 'events'
                    ? 'text-[#B8860B]'
                    : isScrolled
                    ? 'text-gray-700 hover:text-[#B8860B]'
                    : 'text-white hover:text-[#B8860B]'
                }`}
                style={{ fontFamily: 'Lato, sans-serif' }}
              >
                Events
              </button>
              <Link
                to={isAuthenticated ? '/families' : '/signin'}
                className="px-4 py-2 bg-[#B8860B] text-white rounded-lg hover:bg-[#9A7209] transition-colors text-sm font-medium"
                style={{ fontFamily: 'Lato, sans-serif' }}
              >
                {isAuthenticated ? 'Dashboard' : 'Sign In'}
              </Link>
            </div>
            {/* Mobile menu button */}
            <div className="md:hidden flex items-center gap-3">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`p-2 rounded-lg transition-colors ${isScrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/20'}`}
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
              <Link
                to={isAuthenticated ? '/families' : '/signin'}
                className="px-4 py-2 bg-[#B8860B] text-white rounded-lg hover:bg-[#9A7209] transition-colors text-sm font-medium"
              >
                {isAuthenticated ? 'Dashboard' : 'Sign In'}
              </Link>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className={`md:hidden mt-4 pb-4 border-t ${isScrolled ? 'border-gray-200' : 'border-white/20'}`}>
              <div className="flex flex-col space-y-2 pt-4">
                <button
                  onClick={() => scrollToSection('home')}
                  className={`text-left px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    activeSection === 'home'
                      ? 'text-[#B8860B] bg-[#B8860B]/10'
                      : isScrolled
                      ? 'text-gray-700 hover:bg-gray-100'
                      : 'text-white hover:bg-white/10'
                  }`}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  Home
                </button>
                <button
                  onClick={() => scrollToSection('history')}
                  className={`text-left px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    activeSection === 'history'
                      ? 'text-[#B8860B] bg-[#B8860B]/10'
                      : isScrolled
                      ? 'text-gray-700 hover:bg-gray-100'
                      : 'text-white hover:bg-white/10'
                  }`}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  History
                </button>
                <button
                  onClick={() => scrollToSection('news')}
                  className={`text-left px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    activeSection === 'news'
                      ? 'text-[#B8860B] bg-[#B8860B]/10'
                      : isScrolled
                      ? 'text-gray-700 hover:bg-gray-100'
                      : 'text-white hover:bg-white/10'
                  }`}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  News
                </button>
                <button
                  onClick={() => scrollToSection('events')}
                  className={`text-left px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    activeSection === 'events'
                      ? 'text-[#B8860B] bg-[#B8860B]/10'
                      : isScrolled
                      ? 'text-gray-700 hover:bg-gray-100'
                      : 'text-white hover:bg-white/10'
                  }`}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  Events
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative h-screen w-full">
        {displayImages.length > 0 ? (
          <Swiper
            modules={[Navigation, Pagination, Autoplay, EffectFade]}
            spaceBetween={0}
            slidesPerView={1}
            navigation
            pagination={{ clickable: true }}
            autoplay={{ delay: 5000, disableOnInteraction: false }}
            effect="fade"
            loop={displayImages.length > 1}
            className="h-full w-full"
          >
            {displayImages.map((item, index) => {
              const imageUrl = typeof item === 'string' ? item : item.image;
              const key = typeof item === 'string' ? `fallback-${index}` : item._id;

              return (
                <SwiperSlide key={key} className="relative">
                  <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${imageUrl})` }}>
                    <div className="absolute inset-0 bg-black/40"></div>
                  </div>
                  <div className="relative h-full flex items-center justify-center text-center text-white px-4">
                    <div className="max-w-4xl">
                      <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in" style={{ fontFamily: 'Playfair Display, serif' }}>
                        Welcome to St Antony's Church
                      </h1>
                      <p className="text-xl md:text-2xl mb-8 animate-fade-in-delay" style={{ fontFamily: 'Lato, sans-serif' }}>
                        A Community of Faith, Hope, and Love
                      </p>
                      <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in-delay-2">
                        <button
                          onClick={() => scrollToSection('events')}
                          className="px-8 py-3 bg-[#B8860B] text-white rounded-lg hover:bg-[#9A7209] transition-colors text-lg font-medium shadow-lg"
                          style={{ fontFamily: 'Lato, sans-serif' }}
                        >
                          See New Events
                        </button>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        ) : (
          <div className="relative h-full flex items-center justify-center text-center text-white px-4 bg-gradient-to-br from-gray-800 to-gray-900">
            <div className="max-w-4xl">
              <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in" style={{ fontFamily: 'Playfair Display, serif' }}>
                Welcome to St Antony's Church
              </h1>
              <p className="text-xl md:text-2xl mb-8 animate-fade-in-delay" style={{ fontFamily: 'Lato, sans-serif' }}>
                A Community of Faith, Hope, and Love
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in-delay-2">
                <button
                  onClick={() => scrollToSection('events')}
                  className="px-8 py-3 bg-[#B8860B] text-white rounded-lg hover:bg-[#9A7209] transition-colors text-lg font-medium shadow-lg"
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  See New Events
                </button>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Welcome Section */}
      <section id="welcome" className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <img src="/images/church/IMG_0509.jpg" alt="Church interior" className="rounded-lg shadow-xl w-full h-[500px] object-cover" />
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-800" style={{ fontFamily: 'Playfair Display, serif' }}>
                Welcome to Our Church
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed" style={{ fontFamily: 'Lato, sans-serif' }}>
                For over three decades, St Antony's Church has been a beacon of faith, hope, and love in our society. We are dedicated to fostering
                spiritual growth, building strong relationships, and serving those in need.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed" style={{ fontFamily: 'Lato, sans-serif' }}>
                Our mission is to create a welcoming space where everyone can experience God's grace, grow in their faith, and make a positive impact
                in the world around us.
              </p>

              {/* Icon-based highlights */}
              <div className="grid grid-cols-2 gap-6">
                {[
                  { icon: '/images/icons/faith.png', title: 'Faith', description: 'Deepening our relationship with God' },
                  { icon: '/images/icons/community.png', title: 'Community', description: 'Building strong bonds together' },
                  { icon: '/images/icons/service.png', title: 'Service', description: 'Serving others with love' },
                  { icon: '/images/icons/worship.png', title: 'Worship', description: 'Praising and glorifying God' },
                ].map((item, index) => (
                  <div key={index} className="text-center p-4 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow">
                    <div className="flex justify-center mb-3">
                      <img src={item.icon} alt={item.title} className="w-16 h-16 object-contain" />
                    </div>
                    <h3 className="font-semibold text-gray-800 mb-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-600" style={{ fontFamily: 'Lato, sans-serif' }}>
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Church History Timeline */}
      <section id="history" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <div className="text-center mb-8">
              <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-800" style={{ fontFamily: 'Playfair Display, serif' }}>
                Our History
              </h2>
            </div>
            <p
              className="text-base md:text-lg text-gray-700 max-w-4xl mx-auto text-left leading-relaxed px-4"
              style={{ fontFamily: 'Lato, sans-serif', lineHeight: '1.8' }}
            >
              കണ്ണൂർ രൂപതയിലെ പരിയാരം കടന്നപ്പള്ളി വില്ലേജുകളിലായി സ്ഥിതിചെയ്യുന്ന മുടിക്കാനം സെന്റ്. ആന്റണീസ് ദേവാലയം 1987 നവംബർ 27നാണ് പുണ്യ
              ശ്ലോകനായ ദൈവദാസൻ ലീനസ് മരിയ സുക്കോളിന്റെ സഹായത്താൽ നിർമ്മിതമായത്. 1981 മുതലാണ് മുടിക്കാനത്ത് വിശ്വാസ സമൂഹത്തിന്റെ സാന്നിധ്യം
              ആരംഭിക്കുന്നത്. ബഹുമാനപ്പെട്ട ദൈവദാസൻ ലീനസ് സുക്കോൾ വിലയ്ക്കു വാങ്ങിയ 11 ഏക്കർ സ്ഥലത്ത് 14 കുടുംബങ്ങളുമായി ആരംഭിച്ച ഇടവക പരിധിയിൽ ഇന്ന്
              ഇരുന്നൂറററുപതോളം കുടുംബങ്ങളുണ്ട്. ആദ്യകാലങ്ങളിൽ സെന്റ്. ഫ്രാൻസീസ്സ് സേവ്യർ ദേവാലയത്തിന്റെ സബ് സ്റ്റേഷനായിരുന്ന മുടിക്കാനം ദേവാലയം 1995ൽ
              അഭിവന്ദ്യ വർഗ്ഗീസ് ചക്കാലക്കൽ പിതാവിന്റെ കാലത്ത് സ്വതന്ത്ര ഇടവകയായി ഉയർത്തപ്പെട്ടു. 2005 നവംബർ 23ന് നിലവിലുള്ള പുതിയ ദേവാലയത്തിന്റെ
              നിർമ്മാണം പൂർത്തിയാക്കുകയും ആശിർവാദകർമ്മം നടത്തുകയും ചെയ്തു. സ്വതന്ത്ര ഇടവകയായി ഉയർത്തപ്പെട്ടതിനു ശേഷം പതിനാലോളം വൈദികർ ഇടവകയുടെ
              വികാരിമാരായ് സേവനം ചെയ്തിട്ടുണ്ട്. ഇടവക പ്രവർത്തനങ്ങളെ കാര്യക്ഷമമായി ഏകോപിപ്പിക്കുന്ന ഇടവക സമിതി യോടൊപ്പം ആത്മീയ ചൈതന്യത്തോടും സാമൂഹിക
              പ്രതിബദ്ധതയോടുകൂടി പ്രവർത്തിക്കുന്ന വിൻസെന്റ് ഡി. പോൾ, പിതൃവേദി, മാതൃവേദി, കെ. സി. വൈ. എം, എസ്. എ. സി. എന്നീ ഭക്ത സംഘടനകളും സ്തുത്യർഹമായ
              രീതിയിൽ പ്രവർത്തിച്ചു വരുന്നു.
            </p>
          </div>

          {/* Horizontal Scrollable Timeline with Swiper */}
          {histories.length > 0 && (
            <div className="relative">
              <style>{`
                .history-swiper .swiper-slide {
                  height: auto;
                  display: flex;
                }
                .history-swiper .swiper-slide > div {
                  width: 100%;
                }
              `}</style>
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={30}
                slidesPerView={1}
                breakpoints={{
                  640: {
                    slidesPerView: 2,
                    spaceBetween: 20,
                  },
                  768: {
                    slidesPerView: 3,
                    spaceBetween: 30,
                  },
                  1024: {
                    slidesPerView: 4,
                    spaceBetween: 30,
                  },
                }}
                navigation={{
                  nextEl: '.history-swiper-button-next',
                  prevEl: '.history-swiper-button-prev',
                }}
                pagination={{
                  clickable: true,
                  el: '.history-swiper-pagination',
                }}
                autoplay={{
                  delay: 3000,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                }}
                loop={true}
                className="history-swiper pb-12"
              >
                {historyTimeline.map((item, index) => (
                  <SwiperSlide key={index}>
                    <div className="bg-white rounded-lg shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 h-full flex flex-col w-full min-w-[280px] min-h-[450px]">
                      <div className="h-48 overflow-hidden flex-shrink-0 bg-gray-100 flex items-center justify-center">
                        {item.image && item.image !== '/images/icons/man.svg' ? (
                          <img
                            src={item.image}
                            alt={item.priest}
                            className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                            <span className="text-gray-400">No Image</span>
                          </div>
                        )}
                      </div>
                      <div className="p-6 flex flex-col flex-grow min-h-[200px]">
                        <div className="text-[#B8860B] font-bold text-lg mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                          {item.period}
                        </div>
                        <h3 className="text-xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                          {item.priest}
                        </h3>
                        <p className="text-gray-600 leading-relaxed text-sm flex-grow" style={{ fontFamily: 'Lato, sans-serif' }}>
                          {item.description || '\u00A0'}
                        </p>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>

              {/* Custom Navigation Buttons */}
              <button className="history-swiper-button-prev absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#B8860B] hover:text-white transition-all duration-300 -translate-x-4 md:-translate-x-6">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button className="history-swiper-button-next absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#B8860B] hover:text-white transition-all duration-300 translate-x-4 md:translate-x-6">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* Custom Pagination */}
              <div className="history-swiper-pagination flex justify-center gap-2 mt-6"></div>
            </div>
          )}
        </div>
      </section>

      {/* Latest News Section */}
      <section id="news" className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-800" style={{ fontFamily: 'Playfair Display, serif' }}>
              Latest News
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" style={{ fontFamily: 'Lato, sans-serif' }}>
              Stay updated with our latest announcements and community stories
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {news.length === 0 ? (
              <div className="col-span-full text-center text-gray-500 py-12" style={{ fontFamily: 'Lato, sans-serif' }}>
                No news available at the moment.
              </div>
            ) : (
              news.map((item) => (
                <div key={item._id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow animate-fade-in-up">
                  <div className="h-60 overflow-hidden">
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-400">No Image</span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="text-sm text-[#B8860B] mb-2" style={{ fontFamily: 'Lato, sans-serif' }}>
                      {item.date}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {item.title}
                    </h3>
                    <p className="text-gray-600 mb-4 leading-relaxed" style={{ fontFamily: 'Lato, sans-serif' }}>
                      {item.description}
                    </p>
                    <button
                      onClick={() => setSelectedNewsItem(item._id)}
                      className="text-[#B8860B] font-semibold hover:text-[#9A7209] transition-colors flex items-center gap-2"
                      style={{ fontFamily: 'Lato, sans-serif' }}
                    >
                      Read More
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Upcoming Events Section */}
      <section id="events" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-800" style={{ fontFamily: 'Playfair Display, serif' }}>
              Upcoming Events
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" style={{ fontFamily: 'Lato, sans-serif' }}>
              Join us for worship, fellowship, and special celebrations
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {events.length === 0 ? (
              <div className="col-span-full text-center text-gray-500 py-12" style={{ fontFamily: 'Lato, sans-serif' }}>
                No events available at the moment.
              </div>
            ) : (
              events.map((event) => {
                const hasDescription = event.description && event.description.trim().length > 0;
                console.log(`Rendering event card for "${event.title}":`, {
                  hasDescription,
                  description: event.description,
                  descriptionValue: event.description,
                  willRender: hasDescription,
                });
                return (
                  <div
                    key={event._id}
                    className="bg-gradient-to-br from-gray-50 to-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1"
                  >
                    <div className="h-52 overflow-hidden relative">
                      {event.image ? (
                        <img src={event.image} alt={event.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                          <span className="text-gray-400">No Image</span>
                        </div>
                      )}
                      {event.type === 'special' && (
                        <div className="absolute top-2 right-2 bg-[#B8860B] text-white px-3 py-1 rounded-full text-xs font-semibold">
                          Special Event
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2 text-[#B8860B] mb-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        <span className="text-sm font-semibold" style={{ fontFamily: 'Lato, sans-serif' }}>
                          {event.date}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600 mb-3">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm" style={{ fontFamily: 'Lato, sans-serif' }}>
                          {event.time}
                        </span>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {event.title}
                      </h3>
                      {(() => {
                        const desc = event.description;
                        const hasDesc = desc && desc.trim().length > 0;
                        console.log(`[Render] Event "${event.title}" description check:`, {
                          description: desc,
                          hasDescription: hasDesc,
                          type: typeof desc,
                          length: desc?.length,
                          trimmed: desc?.trim(),
                          trimmedLength: desc?.trim().length,
                        });
                        return hasDesc ? (
                          <p
                            className="text-gray-600 text-sm mb-3"
                            style={{
                              fontFamily: 'Lato, sans-serif',
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {event.description}
                          </p>
                        ) : null;
                      })()}
                      <div className="flex items-center gap-2 text-gray-600">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="text-sm" style={{ fontFamily: 'Lato, sans-serif' }}>
                          {event.location}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </section>

      {/* Council Section */}
      <section id="council" className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-800" style={{ fontFamily: 'Playfair Display, serif' }}>
              Church Council
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" style={{ fontFamily: 'Lato, sans-serif' }}>
              Meet our dedicated council members who serve our community
            </p>
          </div>

          {councils.length === 0 ? (
            <div className="text-center text-gray-500 py-12" style={{ fontFamily: 'Lato, sans-serif' }}>
              No council information available at the moment.
            </div>
          ) : (
            <div className="max-w-4xl mx-auto">
              <Link
                to={`/council/${councils[0]?._id}`}
                className="block bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer"
              >
                <div className="h-96 overflow-hidden">
                  {councils[0]?.image ? (
                    <img src={councils[0].image} alt={`Council ${councils[0].period}`} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#B8860B] to-[#9A7209] flex items-center justify-center">
                      <span className="text-white text-2xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
                        Council {councils[0]?.period}
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-2 text-[#B8860B] mb-4">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <span className="text-lg font-semibold" style={{ fontFamily: 'Lato, sans-serif' }}>
                      {councils[0]?.period}
                    </span>
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Church Council
                  </h3>
                  {councils[0]?.description && (
                    <p
                      className="text-gray-600 mb-4"
                      style={{
                        fontFamily: 'Lato, sans-serif',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {councils[0].description}
                    </p>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500" style={{ fontFamily: 'Lato, sans-serif' }}>
                      {councils[0]?.members?.length || 0} Members
                    </span>
                    <span className="text-[#B8860B] font-semibold flex items-center gap-2" style={{ fontFamily: 'Lato, sans-serif' }}>
                      View Details
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                  </div>
                </div>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Footer Section */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            {/* Church Info */}
            <div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#B8860B' }}>
                St Antony's Church
              </h3>
              <p className="text-gray-400 mb-4 leading-relaxed" style={{ fontFamily: 'Lato, sans-serif' }}>
                {churchInfo?.address || '123 Faith Street, City, State 12345'}
              </p>
              {churchInfo?.phone && churchInfo.phone.trim() !== '' && (
                <p className="text-gray-400 mb-2" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Phone: {churchInfo.phone}
                </p>
              )}
              {churchInfo?.email && churchInfo.email.trim() !== '' && (
                <p className="text-gray-400" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Email: {churchInfo.email}
                </p>
              )}
            </div>

            {/* Service Times */}
            <div>
              <h4 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Service Times
              </h4>
              <div className="space-y-2" style={{ fontFamily: 'Lato, sans-serif' }}>
                {churchInfo?.serviceTimes && churchInfo.serviceTimes.length > 0 ? (
                  churchInfo.serviceTimes.map((serviceTime, index) => (
                    <p key={index} className="text-gray-400">
                      <span className="font-semibold text-white">{serviceTime.day}:</span> {serviceTime.time}
                      {serviceTime.description && ` (${serviceTime.description})`}
                    </p>
                  ))
                ) : (
                  <>
                    <p className="text-gray-400">
                      <span className="font-semibold text-white">Sunday:</span> 10:00 AM
                    </p>
                    <p className="text-gray-400">
                      <span className="font-semibold text-white">Wednesday:</span> 7:00 PM (Bible Study)
                    </p>
                    <p className="text-gray-400">
                      <span className="font-semibold text-white">Friday:</span> 7:00 PM (Prayer Meeting)
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Quick Links
              </h4>
              <ul className="space-y-2" style={{ fontFamily: 'Lato, sans-serif' }}>
                <li>
                  <button onClick={() => scrollToSection('history')} className="text-gray-400 hover:text-[#B8860B] transition-colors">
                    Our History
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('news')} className="text-gray-400 hover:text-[#B8860B] transition-colors">
                    News & Updates
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('events')} className="text-gray-400 hover:text-[#B8860B] transition-colors">
                    Events
                  </button>
                </li>
                <li>
                  <Link
                    to={councils.length > 0 ? `/council/${councils[0]._id}` : '#'}
                    className="text-gray-400 hover:text-[#B8860B] transition-colors"
                  >
                    Council
                  </Link>
                </li>
              </ul>
            </div>

            {/* Newsletter & Social */}
            <div>
              <h4 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Stay Connected
              </h4>

              <div className="flex gap-4">
                {churchInfo?.socialLinks && churchInfo.socialLinks.length > 0 ? (
                  churchInfo.socialLinks.map((socialLink, index) => {
                    const getIcon = (platform: string) => {
                      switch (platform) {
                        case 'Facebook':
                          return (
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                            </svg>
                          );
                        case 'Instagram':
                          return (
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                            </svg>
                          );
                        case 'Twitter':
                          return (
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                            </svg>
                          );
                        case 'YouTube':
                          return (
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                            </svg>
                          );
                        case 'LinkedIn':
                          return (
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                            </svg>
                          );
                        case 'WhatsApp':
                          return (
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                            </svg>
                          );
                        default:
                          return (
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm-1-11V7h2v4h4v2h-4v4h-2v-4H7v-2h4z" />
                            </svg>
                          );
                      }
                    };

                    return (
                      <a
                        key={index}
                        href={socialLink.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-[#B8860B] transition-colors"
                        aria-label={socialLink.platform}
                      >
                        {getIcon(socialLink.platform)}
                      </a>
                    );
                  })
                ) : (
                  // Default social links if none are configured
                  <>
                    <a
                      href="https://facebook.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-[#B8860B] transition-colors"
                      aria-label="Facebook"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                    </a>
                    <a
                      href="https://instagram.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-[#B8860B] transition-colors"
                      aria-label="Instagram"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                      </svg>
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Map Preview */}
          <div className="mt-12 mb-8">
            <h4 className="text-lg font-semibold mb-4 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
              Find Us
            </h4>
            <div className="w-full rounded-lg overflow-hidden shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3901.3842759383897!2d75.31552119999999!3d12.0858241!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba46bc029f09961%3A0xeeb0a668742582f5!2sSt.%20Antony&#39;s%20Roman%20Catholic%20Church%2C%20Mudikanam!5e0!3m2!1sen!2sin!4v1765172286522!5m2!1sen!2sin"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full"
                title="St Antony's Church Location Map"
              />
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400" style={{ fontFamily: 'Lato, sans-serif' }}>
              © {new Date().getFullYear()} St Antony's Church. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* News Modal */}
      <Modal
        isOpen={selectedNewsItem !== null}
        onClose={() => setSelectedNewsItem(null)}
        className="max-w-2xl w-full mx-4 my-4 md:my-8 max-h-[90vh] md:max-h-[850px] flex flex-col"
      >
        {selectedNewsItem !== null &&
          (() => {
            const newsItem = news.find((item) => item._id === selectedNewsItem);
            if (!newsItem) return null;

            return (
              <div className="flex flex-col h-full max-h-[90vh] md:max-h-[850px] rounded-3xl overflow-hidden">
                {/* Scrollable content area */}
                <div className="overflow-y-auto flex-1 p-4 md:p-6 scrollbar-hide rounded-t-3xl">
                  <div className="mb-4">
                    <div className="text-xs md:text-sm text-[#B8860B] mb-2" style={{ fontFamily: 'Lato, sans-serif' }}>
                      {newsItem.date}
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {newsItem.title}
                    </h2>
                  </div>

                  {newsItem.image && (
                    <div className="mb-4 rounded-lg overflow-hidden">
                      <img src={newsItem.image} alt={newsItem.title} className="w-full h-48 md:h-64 object-cover" />
                    </div>
                  )}

                  <div
                    className="text-gray-700 leading-relaxed text-sm md:text-base prose prose-sm max-w-none"
                    style={{ fontFamily: 'Lato, sans-serif', lineHeight: '1.8' }}
                    dangerouslySetInnerHTML={{ __html: newsItem.fullContent }}
                  />
                </div>

                {/* Fixed footer with close button */}
                <div className="p-4 md:p-6 pt-4 border-t border-gray-200 bg-white flex-shrink-0 rounded-b-3xl">
                  <button
                    onClick={() => setSelectedNewsItem(null)}
                    className="w-full md:w-auto px-6 py-2.5 md:py-3 bg-[#B8860B] text-white rounded-lg hover:bg-[#9A7209] transition-colors font-semibold text-sm md:text-base"
                    style={{ fontFamily: 'Lato, sans-serif' }}
                  >
                    Close
                  </button>
                </div>
              </div>
            );
          })()}
      </Modal>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 1s ease-out;
        }

        .animate-fade-in-delay {
          animation: fade-in 1s ease-out 0.3s both;
        }

        .animate-fade-in-delay-2 {
          animation: fade-in 1s ease-out 0.6s both;
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out both;
        }

        .swiper-button-next,
        .swiper-button-prev {
          color: white !important;
          background: rgba(184, 134, 11, 0.8);
          width: 50px !important;
          height: 50px !important;
          border-radius: 50%;
        }

        .swiper-button-next:after,
        .swiper-button-prev:after {
          font-size: 20px !important;
        }

        /* Hide hero section navigation arrows on mobile */
        @media (max-width: 768px) {
          section#home .swiper-button-next,
          section#home .swiper-button-prev {
            display: none !important;
          }
        }

        /* Hero Section Swiper Pagination (white bullets for dark overlay) */
        .swiper:not(.history-swiper) .swiper-pagination-bullet {
          background: white !important;
          opacity: 0.7 !important;
        }

        .swiper:not(.history-swiper) .swiper-pagination-bullet-active {
          background: #B8860B !important;
          opacity: 1 !important;
        }

        /* History Section Swiper Styles */
        .history-swiper {
          padding-bottom: 3rem;
        }

        .history-swiper .swiper-slide {
          height: auto;
          display: flex;
        }

        .history-swiper .swiper-wrapper {
          display: flex;
          align-items: stretch;
        }

        .history-swiper-button-prev,
        .history-swiper-button-next {
          color: #344054 !important;
          background: white !important;
          box-shadow: 0px 4px 8px -2px rgba(16, 24, 40, 0.10), 0px 2px 4px -2px rgba(16, 24, 40, 0.06);
        }

        .history-swiper-button-prev:hover,
        .history-swiper-button-next:hover {
          color: white !important;
          background: #B8860B !important;
        }

        .history-swiper-button-prev.swiper-button-disabled,
        .history-swiper-button-next.swiper-button-disabled {
          opacity: 0.5 !important;
        }

        /* History section pagination - black bullets */
        section#history .history-swiper .swiper-pagination .swiper-pagination-bullet,
        section#history .history-swiper-pagination .swiper-pagination-bullet,
        .history-swiper .swiper-pagination .swiper-pagination-bullet,
        .history-swiper-pagination .swiper-pagination-bullet {
          width: 12px !important;
          height: 12px !important;
          background: #000000 !important;
          background-color: #000000 !important;
          opacity: 1 !important;
          border: none !important;
          transition: all 0.3s ease !important;
        }

        section#history .history-swiper .swiper-pagination .swiper-pagination-bullet-active,
        section#history .history-swiper-pagination .swiper-pagination-bullet-active,
        .history-swiper .swiper-pagination .swiper-pagination-bullet-active,
        .history-swiper-pagination .swiper-pagination-bullet-active {
          background: #B8860B !important;
          background-color: #B8860B !important;
          width: 24px !important;
          border-radius: 6px !important;
          opacity: 1 !important;
        }

        @media (max-width: 768px) {
          .history-swiper-button-prev,
          .history-swiper-button-next {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
